import MsgTrans from '@components/common/i18n/message-trans'
import { FilledRightArrow } from '@components/common/icons/custom-icons'
import { LinguiJSMessageDescriptor } from '@lib/types/common'
import { defineMessage, Trans } from '@lingui/macro'
import classNames from 'classnames'

type CommonTable = {
  label: LinguiJSMessageDescriptor
  contents: LinguiJSMessageDescriptor[]
}

const CLASSES: CommonTable[] = [
  {
    label: defineMessage({ message: `Class duration` }),
    contents: [
      defineMessage({ message: `45 minutes` }),
      defineMessage({ message: `60 minutes` }),
      defineMessage({ message: `60 minutes` }),
    ],
  },
  {
    label: defineMessage({ message: `Class type` }),
    contents: [
      defineMessage({ message: `Individual` }),
      defineMessage({ message: `Group` }),
      defineMessage({ message: `Group` }),
    ],
  },
  {
    label: defineMessage({ message: `Class schedule` }),
    contents: [
      defineMessage({ message: `Flexible` }),
      defineMessage({ message: `Peer dependent` }),
      defineMessage({ message: `Peer dependent` }),
    ],
  },
]

const methodologies: CommonTable[] = [
  {
    label: defineMessage({ message: `Learning mode` }),
    contents: [
      defineMessage({ message: `With teacher` }),
      defineMessage({ message: `With teacher and peers` }),
      defineMessage({ message: `With teacher and peers` }),
    ],
  },
  {
    label: defineMessage({ message: `Pace of learning` }),
    contents: [
      defineMessage({ message: `Fully personalized` }),
      defineMessage({ message: `Personalized` }),
      defineMessage({ message: `Personalized` }),
    ],
  },
  {
    label: defineMessage({ message: `Activities` }),
    contents: [
      defineMessage({ message: `With teacher` }),
      defineMessage({ message: `With teacher and peer` }),
      defineMessage({ message: `With teacher and peers` }),
    ],
  },
  {
    label: defineMessage({ message: `Assessment` }),
    contents: [
      defineMessage({ message: `Individual` }),
      defineMessage({ message: `Individual and as a team` }),
      defineMessage({ message: `Individual and as a team` }),
    ],
  },
  {
    label: defineMessage({ message: `Recognition` }),
    contents: [
      defineMessage({ message: `Individual` }),
      defineMessage({ message: `Individual and group` }),
      defineMessage({ message: `Individual and group` }),
    ],
  },
]

const skills: CommonTable[] = [
  {
    label: defineMessage({ message: `Collaboration` }),
    contents: [
      defineMessage({ message: `With teacher` }),
      defineMessage({ message: `With teacher and peer` }),
      defineMessage({ message: `With teacher and peers` }),
    ],
  },
  {
    label: defineMessage({ message: `Interaction` }),
    contents: [
      defineMessage({ message: `Interaction with teacher` }),
      defineMessage({ message: `Interaction with teacher and peer` }),
      defineMessage({ message: `Interaction with teacher and peers` }),
    ],
  },
  {
    label: defineMessage({ message: `Teamwork` }),
    contents: [
      defineMessage({ message: `Collaboration with teacher` }),
      defineMessage({ message: `Collaboration with teacher and peer` }),
      defineMessage({ message: `Collaboration with teacher and peers` }),
    ],
  },
]

const TableLine = ({ classItem }: { classItem: CommonTable }) => (
  <div className="grid grid-cols-5">
    <p className="text-grey-800">
      <MsgTrans msg={classItem.label} />
    </p>
    <div className="grid grid-cols-2 col-span-4 gap-x-10">
      {classItem.contents.slice(0, 2).map((content) => (
        <div
          key={content?.id}
          className="flex items-baseline pb-2 border-b border-grey-500"
        >
          <FilledRightArrow className="w-3 h-3 mr-3 text-orange-300" />
          <span>
            <MsgTrans msg={content} />
          </span>
        </div>
      ))}
    </div>
  </div>
)

type ClassesTableProps = {
  className?: string
  padding?: boolean
}
const ClassesTable = ({ className, padding = true }: ClassesTableProps) => {
  return (
    <div
      className={classNames(
        'w-full lg:max-w-5xl mx-auto',
        { 'pl-16': padding },
        className
      )}
    >
      <div className="grid grid-cols-5 text-xl font-600 text-orange">
        {/* Empty div here to align with the 4 column table with only 3 column table headers */}
        <div />
        <div className="grid grid-cols-2 col-span-4 gap-x-10">
          <h4>
            <Trans>Private 1-on-1</Trans>
          </h4>
          <p>
            <Trans>Peer learning program</Trans>
          </p>
        </div>
      </div>
      <div className="flex flex-col space-y-10">
        <div className="flex flex-col space-y-5">
          <h3 className="font-600">
            <Trans>Classes</Trans>
          </h3>
          {CLASSES.map((classItem) => (
            <TableLine classItem={classItem} key={classItem.label?.id} />
          ))}
        </div>

        <div className="flex flex-col space-y-5">
          <h4 className="font-600">
            <Trans>Methodology</Trans>
          </h4>
          {methodologies.map((classItem) => (
            <TableLine classItem={classItem} key={classItem.label?.id} />
          ))}
        </div>

        <div className="flex flex-col space-y-5">
          <h4 className="font-600">
            <Trans>Skills</Trans>
          </h4>
          {skills.map((classItem) => (
            <TableLine classItem={classItem} key={classItem.label?.id} />
          ))}
        </div>
      </div>
    </div>
  )
}

export default ClassesTable
